import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreateMarket, useMarkets } from '@/api/domains/markets';
import { useCompleteOnboarding } from '@/api/domains/onboarding';
import { InPostLogo } from '@/components/inpost-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Radio } from '@/components/ui/radio';
import { resolveLocale } from '@/i18n';
import { toApiProblem } from '@/lib/errors';

import { CARRIER_COUNTRIES } from '@/pages/markets/schemas';
import { suggestMarketName } from '@/pages/markets/market-names';

type CountryOption = { code: string; name: string };

/** Setup wizard step 3 - Figma 347-2970. Single-market (radio) selection. */
export function SelectMarketStep() {
  const { t, i18n } = useTranslation('setup');
  const locale = resolveLocale(i18n.language);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const marketsQuery = useMarkets();
  const createMarket = useCreateMarket();
  const completeOnboarding = useCompleteOnboarding();
  const isSubmitting = createMarket.isPending || completeOnboarding.isPending;

  const finishOnboarding = useCallback(() => {
    completeOnboarding.mutate(undefined, {
      onSuccess: () => navigate('/markets'),
      // The market is in place; don't trap the merchant if the flag write fails.
      onError: () => navigate('/markets'),
    });
  }, [completeOnboarding, navigate]);

  // A merchant who already configured a market doesn't need to create a first
  // one - finish onboarding and drop them on the markets page instead of forcing
  // a redundant selection.
  const hasMarket = (marketsQuery.data?.length ?? 0) > 0;
  const skipped = useRef(false);
  useEffect(() => {
    if (!hasMarket || skipped.current) return;
    skipped.current = true;
    finishOnboarding();
  }, [hasMarket, finishOnboarding]);

  const options = useMemo<CountryOption[]>(() => {
    const list = CARRIER_COUNTRIES.map((code) => ({
      code,
      name: suggestMarketName([code], locale),
    }));
    return list.sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, search]);

  const handleContinue = () => {
    if (!selected || isSubmitting) return;
    createMarket.mutate(
      { countries: [selected], name: suggestMarketName([selected], locale) },
      {
        onSuccess: () => finishOnboarding(),
        onError: (error) => {
          const problem = toApiProblem(error);
          toast.error(t('market.createFailed'), { description: problem.detail ?? problem.title });
        },
      },
    );
  };

  // Wait for the markets lookup before showing the create form; if one already
  // exists we're navigating away, so render a lightweight placeholder meanwhile.
  if (marketsQuery.isLoading || hasMarket) {
    return <div className="text-muted-foreground">…</div>;
  }

  return (
    <div className="flex flex-col gap-[17px]">
      <InPostLogo variant="white-bg" className="h-9 w-auto self-start" />
      <h1 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('market.title')}</h1>
      <p className="max-w-[495px] text-base text-muted-foreground">
        <Trans
          t={t}
          i18nKey="market.description"
          components={{ strong: <span className="font-semibold text-foreground underline" /> }}
        />
      </p>

      <Input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('market.searchPlaceholder')}
        className="max-w-[401px]"
      />

      <div className="flex max-h-[280px] max-w-[401px] flex-col overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">{t('market.empty')}</p>
        ) : (
          filtered.map((option) => (
            <label
              key={option.code}
              className="flex cursor-pointer items-center gap-3 border-b border-border py-3"
            >
              <Radio
                name="setup-market"
                value={option.code}
                checked={selected === option.code}
                onChange={() => setSelected(option.code)}
              />
              <span className="text-base text-foreground">{option.name}</span>
            </label>
          ))
        )}
      </div>

      <Button
        type="button"
        className="w-[163px] rounded-[10px]"
        disabled={!selected || isSubmitting}
        onClick={handleContinue}
      >
        {isSubmitting ? t('market.creating') : t('market.continue')}
      </Button>
    </div>
  );
}
